import axios from 'axios';

// Use relative URL in production (same domain), absolute URL in development
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is explicitly set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In development (localhost), use local backend
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000/api';
  }

  // In production/staging (any other host), use same-origin API
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // The session cookie is the credential now (MP-MIG-001). It is HttpOnly, so
  // nothing here can read it — the browser attaches it and the BFF resolves it
  // server-side. Without this, same-origin requests still send it but
  // cross-origin development against localhost:8000 does not, and the failure
  // looks like "logged in but every call is a 401".
  withCredentials: true,
});

// No Authorization header. The BFF holds the access and refresh tokens
// server-side in the Django session and the browser gets one HttpOnly cookie
// (ADR-0001 §4.1) — which is the point of the pattern: a token the page can
// read is a token a script on the page can take.

// Handle token refresh on 401 and MFA enrollment on 403
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle MFA enrollment required (403)
    if (error.response?.status === 403 && error.response?.data?.mfa_enrollment_required) {
      // Don't redirect if already on enrollment page or if this is an MFA endpoint
      const currentPath = window.location.pathname;
      const isMFAEndpoint = originalRequest.url?.includes('/mfa/');
      const isEnrollmentPage = currentPath === '/mfa/enroll';
      
      if (!isEnrollmentPage && !isMFAEndpoint) {
        // Store intended destination
        const intendedDestination = currentPath !== '/login' ? currentPath : '/platform/admin';
        
        // Redirect to enrollment page
        window.location.href = `/mfa/enroll?from=${encodeURIComponent(intendedDestination)}`;
        return Promise.reject(error); // Stop further processing
      }
    }
    
    // Handle MFA verification required (403)
    if (error.response?.status === 403 && error.response?.data?.mfa_verification_required) {
      const currentPath = window.location.pathname;
      const isMFAEndpoint = originalRequest.url?.includes('/mfa/');
      const isVerificationPage = currentPath === '/mfa/verify';
      
      if (!isVerificationPage && !isMFAEndpoint) {
        // Store intended destination
        const intendedDestination = currentPath !== '/login' ? currentPath : '/platform/admin';
        
        // Redirect to verification page (or show modal - for now redirect to platform admin)
        // TODO: Create /mfa/verify page or show verification modal
        // For now, redirect to platform admin and show error
        console.warn('MFA verification required - redirecting to platform admin');
        window.location.href = `/platform/admin?mfa_verify=true&from=${encodeURIComponent(intendedDestination)}`;
        return Promise.reject(error); // Stop further processing
      }
    }

    // Handle subscription upgrade required (403)
    if (error.response?.status === 403 && error.response?.data?.subscription_upgrade_required) {
      const currentPath = window.location.pathname;
      const isSubscriptionsPage = currentPath === '/settings/subscriptions' || currentPath === '/settings';
      const isSubscriptionEndpoint = originalRequest.url?.includes('/subscription') ||
                                     originalRequest.url?.includes('/stripe/');
      if (!isSubscriptionsPage && !isSubscriptionEndpoint) {
        window.location.href = '/settings/subscriptions';
        return Promise.reject(error);
      }
    }
    
    // A 401 now means the session is gone — expired, signed out elsewhere, or
    // revoked by the control plane. There is nothing to refresh from the
    // browser: the refresh token lives in the session that just ended, so the
    // only move is to start a new login.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const here = window.location.pathname + window.location.search;
      window.location.href = `/auth/login?next=${encodeURIComponent(here)}`;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Feature Flags API methods
export const getFeatureFlags = () => {
  return api.get('/platform/feature-flags/');
};

export const getFeatureFlag = (id) => {
  return api.get(`/platform/feature-flags/${id}/`);
};

export const createFeatureFlag = (data) => {
  return api.post('/platform/feature-flags/', data);
};

export const updateFeatureFlag = (id, data) => {
  return api.patch(`/platform/feature-flags/${id}/`, data);
};

export const deleteFeatureFlag = (id) => {
  return api.delete(`/platform/feature-flags/${id}/`);
};

export const toggleGlobalFlag = (id) => {
  return api.post(`/platform/feature-flags/${id}/toggle_global/`);
};

// Account Entitlements API methods
export const getAccountEntitlements = (params = {}) => {
  return api.get('/platform/account-entitlements/', { params });
};

export const getAccountEntitlement = (id) => {
  return api.get(`/platform/account-entitlements/${id}/`);
};

export const createAccountEntitlement = (data) => {
  return api.post('/platform/account-entitlements/', data);
};

export const updateAccountEntitlement = (id, data) => {
  return api.patch(`/platform/account-entitlements/${id}/`, data);
};

export const deleteAccountEntitlement = (id) => {
  return api.delete(`/platform/account-entitlements/${id}/`);
};

export const bulkAssignEntitlements = (data) => {
  return api.post('/platform/account-entitlements/bulk_assign/', data);
};

// Platform Admin API methods
export const updateTenant = (id, data) => {
  return api.patch(`/platform/tenants/${id}/`, data);
};

export const getTenantSubscription = (tenantId) => {
  return api.get(`/platform/tenants/${tenantId}/subscription/`);
};

export const resendTenantApprovalEmail = (tenantId) => {
  return api.post(`/platform/tenants/${tenantId}/resend_approval_email/`);
};

// MFA API methods
export const mfaEnroll = (deviceName) => {
  return api.post('/v1/mfa/enroll', { device_name: deviceName });
};

export const mfaEnrollVerify = (code) => {
  return api.post('/v1/mfa/enroll/verify', { code });
};

export const mfaVerify = (code, useRecovery = false) => {
  return api.post('/v1/mfa/verify', { code, use_recovery: useRecovery });
};

export const mfaRecoveryVerify = (code) => {
  return api.post('/v1/mfa/recovery/verify', { code });
};

export const getMFADevices = () => {
  return api.get('/v1/mfa/devices/');
};

export const deleteMFADevice = (deviceId) => {
  return api.delete(`/v1/mfa/devices/${deviceId}/`);
};

export const generateRecoveryCodes = () => {
  return api.post('/v1/mfa/recovery/generate');
};

export const getTenantMFAPolicy = () => {
  return api.get('/v1/tenant/mfa-policy/');
};

export const updateTenantMFAPolicy = (data) => {
  return api.patch('/v1/tenant/mfa-policy/', data);
};

export const resetUserMFA = (userId) => {
  return api.post('/v1/mfa/reset', { user_id: userId });
};

// Tenant email accounts (admin-only)
export const getTenantEmailAccounts = () => {
  return api.get('/v1/tenant/email-accounts/');
};

export const getEmailAccountOAuthUrl = (provider) => {
  return api.get(`/v1/tenant/email-accounts/oauth/authorize/?provider=${provider}`);
};

export const createTenantEmailAccount = (data) => {
  return api.post('/v1/tenant/email-accounts/', data);
};

export const updateTenantEmailAccount = (id, data) => {
  return api.patch(`/v1/tenant/email-accounts/${id}/`, data);
};

export const deleteTenantEmailAccount = (id) => {
  return api.delete(`/v1/tenant/email-accounts/${id}/`);
};

export const testSMTPConnection = (data) => {
  return api.post('/v1/tenant/email-accounts/test/', data);
};

export const sendEmail = (data) => {
  return api.post('/v1/emails/send/', data);
};

export default api;

